import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import { useLocationAPIs } from '../../apis/LocationAPIs';
import { useStudentClassAPIs } from '../../apis/StudentClassAPIs';

function AddInventory({ open, onClose, onSave, initialData = null, assetId = null }) {
    const { fetchLocations, addLocation } = useLocationAPIs();
    const { fetchStudentClasses, addStudentClass } = useStudentClassAPIs();
    
    // Default empty asset structure
    const getDefaultAsset = () => ({
        item: '',
        type: '',
        typeDescription: '',
        serialNo: '',
        assetCondition: 'new', // Default condition: new
        manufacturer: '',
        model: '',
        hyperlink: '',
        purchaseDate: '',
        purchaseCost: '',
        location: '',
        allowedTime: '4', // Default rental duration: 4 hours
        checkedOut: false, 
        allowedClasses: '',
        notes: '',
        kitItems: []
    });

    const [newAsset, setNewAsset] = useState(getDefaultAsset());
    const [locations, setLocations] = useState([]);
    const [showAddLocationInput, setShowAddLocationInput] = useState(false);
    const [newLocationName, setNewLocationName] = useState('');
    const [studentClasses, setStudentClasses] = useState([]);
    const [showAddClassInput, setShowAddClassInput] = useState(false);
    const [newClassName, setNewClassName] = useState('');

    // Load locations and classes when dialog opens
    useEffect(() => {
        if (open) {
            const loadLocations = async () => {
                try {
                    const locationsData = await fetchLocations();
                    if (locationsData && Array.isArray(locationsData)) {
                        setLocations(locationsData);
                    }
                } catch (err) {
                    console.error("Failed to load locations:", err);
                }
            };
            const loadStudentClasses = async () => {
                try {
                    const studentClassesData = await fetchStudentClasses();
                    if (studentClassesData && Array.isArray(studentClassesData)) {
                        setStudentClasses(studentClassesData);
                    }
                } catch (err) {
                    console.error("Failed to load student classes:", err);
                }
            };
            loadLocations();
            loadStudentClasses();
        }
    }, [open, fetchLocations, fetchStudentClasses]);

    // Initialize asset data when dialog opens or initialData changes
    useEffect(() => {
        if (open) {
            if (initialData) {
                // Edit mode: populate with existing data
                // Handle both {row: {...}} and direct row object formats
                const assetData = initialData.row || initialData;
                setNewAsset({
                    item: assetData.item || '',
                    type: assetData.type || '',
                    typeDescription: assetData.typeDescription || '',
                    serialNo: assetData.serialNo || '',
                    assetCondition: assetData.assetCondition || 'new',
                    manufacturer: assetData.manufacturer || '',
                    model: assetData.model || '',
                    hyperlink: assetData.hyperlink || '',
                    purchaseDate: assetData.purchaseDate || '',
                    purchaseCost: assetData.purchaseCost || '',
                    location: assetData.location || '',
                    // Handle both camelCase and snake_case field names
                    allowedTime: assetData.allowedTime || assetData.allowed_time || '4',
                    checkedOut: assetData.checkedOut !== undefined ? assetData.checkedOut : false,
                    allowedClasses: assetData.allowedClasses || '',
                    notes: assetData.notes || '',
                    kitItems: assetData.kitItems || []
                });
            } else {
                // Add mode: use default empty values
                setNewAsset(getDefaultAsset());
            }
            setShowAddLocationInput(false);
            setNewLocationName('');
            setShowAddClassInput(false);
            setNewClassName('');
        }
    }, [open, initialData]);

    const handleChange = (event) => {
        setNewAsset({
            ...newAsset,
            [event.target.name]: event.target.value
        });
    };

    const handleSave = async () => {
        if (assetId) {
            // Edit mode: onSave receives (asset, assetId)
            const success = await onSave(newAsset, assetId);
            if (success) {
                onClose();
            }
        } else {
            // Add mode: onSave receives just the asset
            const success = await onSave(newAsset);
            if (success) {
                onClose();
            }
        }
    };

    const updateKitItem = (index, field, newValue) => {
        const updatedKitItems = [...newAsset.kitItems];
        updatedKitItems[index] = {
            ...updatedKitItems[index],
            [field]: newValue
        };
        setNewAsset({ ...newAsset, kitItems: updatedKitItems });
    };

    const handleLocationChange = (event) => {
        const value = event.target.value;
        if (value === '__add_new__') {
            setShowAddLocationInput(true);
        } else {
            setNewAsset({ ...newAsset, location: value });
            setShowAddLocationInput(false);
        }
    };

    const handleAddNewLocation = async () => {
        if (!newLocationName || !newLocationName.trim()) {
            return;
        }

        try {
            const newLocation = await addLocation(newLocationName.trim());
            if (newLocation) {
                // Reload locations to get the new one
                const updatedLocations = await fetchLocations();
                if (updatedLocations && Array.isArray(updatedLocations)) {
                    setLocations(updatedLocations);
                }
                // Set the new location as selected
                setNewAsset({ ...newAsset, location: newLocation.locationName });
                setShowAddLocationInput(false);
                setNewLocationName('');
            }
        } catch (err) {
            console.error("Failed to add new location:", err);
        }
    };

    const handleCancelAddLocation = () => {
        setShowAddLocationInput(false);
        setNewLocationName('');
    };

    const handleClassChange = (event) => {
        const value = event.target.value;
        if (value === '__add_new__') {
            setShowAddClassInput(true);
        } else {
            setNewAsset({ ...newAsset, allowedClasses: value });
            setShowAddClassInput(false);
        }
    };

    const handleAddNewClass = async () => {
        if (!newClassName || !newClassName.trim()) {
            return;
        }

        try {
            const newStudentClass = await addStudentClass(newClassName.trim());
            if (newStudentClass) {
                // Reload student classes to get the new one
                const updatedStudentClasses = await fetchStudentClasses();
                if (updatedStudentClasses && Array.isArray(updatedStudentClasses)) {
                    setStudentClasses(updatedStudentClasses);
                }
                // Set the new student class as selected
                setNewAsset({ ...newAsset, allowedClasses: newStudentClass.name });
                setShowAddClassInput(false);
                setNewClassName('');
            }
        } catch (err) {
            console.error("Failed to add new student class:", err);
        }
    };

    const handleCancelAddClass = () => {
        setShowAddClassInput(false);
        setNewClassName('');
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{initialData ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
            <DialogContent sx={{ width: '800px' }}>
                <Stack spacing={2} mt={1}>
                    {Object.entries(newAsset).map(([key, value]) => {
                        if (key === "kitItems") {
                            return (
                                <div key="kitItems">
                                    <label>Kit Items:</label>
                                    {value.map((item, index) => (
                                        <div key={index} style={{ marginBottom: '16px', padding: '8px', border: '1px solid #ccc', borderRadius: '8px' }}>
                                            <h4>Kit Item {index + 1}</h4>
                                            <Stack spacing={1}>
                                                <TextField
                                                    label="Manufacturer"
                                                    value={item.manufacturer}
                                                    onChange={(e) => updateKitItem(index, 'manufacturer', e.target.value)}
                                                    fullWidth
                                                />
                                                <TextField
                                                    label="Model"
                                                    value={item.model}
                                                    onChange={(e) => updateKitItem(index, 'model', e.target.value)}
                                                    fullWidth
                                                />
                                                <TextField
                                                    label="Description"
                                                    value={item.description}
                                                    onChange={(e) => updateKitItem(index, 'description', e.target.value)}
                                                    fullWidth
                                                />
                                                <TextField
                                                    label="Serial Number"
                                                    value={item.serialNumber}
                                                    onChange={(e) => updateKitItem(index, 'serialNumber', e.target.value)}
                                                    fullWidth
                                                />
                                                <TextField
                                                    label="Cost"
                                                    type="number"
                                                    value={item.cost}
                                                    onChange={(e) => updateKitItem(index, 'cost', parseFloat(e.target.value))}
                                                    fullWidth
                                                />
                                                <TextField
                                                    label="Vendor"
                                                    value={item.vendor}
                                                    onChange={(e) => updateKitItem(index, 'vendor', e.target.value)}
                                                    fullWidth
                                                />
                                                <TextField
                                                    label="In Service Date"
                                                    type="date"
                                                    InputLabelProps={{ shrink: true }}
                                                    value={item.inServiceDate}
                                                    onChange={(e) => updateKitItem(index, 'inServiceDate', e.target.value)}
                                                    fullWidth
                                                />
                                                <TextField
                                                    label="Notes"
                                                    value={item.notes}
                                                    onChange={(e) => updateKitItem(index, 'notes', e.target.value)}
                                                    multiline
                                                    rows={2}
                                                    fullWidth
                                                />
                                            </Stack>
                                        </div>
                                    ))}
                                    <Button
                                        onClick={() =>
                                            setNewAsset({
                                                ...newAsset,
                                                kitItems: [...value, {
                                                    manufacturer: '',
                                                    model: '',
                                                    description: '',
                                                    serialNumber: '',
                                                    cost: '',
                                                    vendor: '',
                                                    inServiceDate: '',
                                                    notes: ''
                                                }]
                                            })
                                        }
                                    >
                                        + Add Kit Item
                                    </Button>
                                </div>
                            );
                        }

                        // Special handling for allowedTime (rental duration)
                        if (key === "allowedTime") {
                            return (
                                <TextField
                                    key={key}
                                    label="Rental Duration (hours)"
                                    name={key}
                                    type="number"
                                    variant="standard"
                                    value={value || "4"}
                                    onChange={handleChange}
                                    inputProps={{ min: 1, step: 1 }}
                                    helperText="Default: 4 hours"
                                />
                            );
                        }

                        // Special handling for assetCondition (dropdown)
                        if (key === "assetCondition") {
                            return (
                                <FormControl key={key} variant="standard" fullWidth>
                                    <InputLabel>Asset Condition</InputLabel>
                                    <Select
                                        name={key}
                                        value={value || "new"}
                                        onChange={handleChange}
                                        label="Asset Condition"
                                    >
                                        <MenuItem value="new">New</MenuItem>
                                        <MenuItem value="good">Good</MenuItem>
                                        <MenuItem value="fair">Fair</MenuItem>
                                        <MenuItem value="poor">Poor</MenuItem>
                                        <MenuItem value="damaged">Damaged</MenuItem>
                                    </Select>
                                </FormControl>
                            );
                        }

                        // Special handling for location (dropdown)
                        if (key === "location") {
                            return (
                                <Box key={key}>
                                    <FormControl variant="standard" fullWidth>
                                        <InputLabel>Location</InputLabel>
                                        <Select
                                            name={key}
                                            value={value || ""}
                                            onChange={handleLocationChange}
                                            label="Location"
                                        >
                                            {locations.map((loc) => (
                                                <MenuItem key={loc.id} value={loc.locationName}>
                                                    {loc.locationName}
                                                </MenuItem>
                                            ))}
                                            <MenuItem value="__add_new__">+ Add New Location</MenuItem>
                                        </Select>
                                    </FormControl>
                                    {showAddLocationInput && (
                                        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <TextField
                                                label="New Location Name"
                                                value={newLocationName}
                                                onChange={(e) => setNewLocationName(e.target.value)}
                                                variant="standard"
                                                fullWidth
                                                autoFocus
                                            />
                                            <Button onClick={handleAddNewLocation} variant="contained" size="small">
                                                Add
                                            </Button>
                                            <Button onClick={handleCancelAddLocation} size="small">
                                                Cancel
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            );
                        }

                        // Special handling for allowedClasses (dropdown)
                        if (key === "allowedClasses") {
                            return (
                                <Box key={key}>
                                    <FormControl variant="standard" fullWidth>
                                        <InputLabel>Allowed Classes</InputLabel>
                                        <Select
                                            name={key}
                                            value={value || ""}
                                            onChange={handleClassChange}
                                            label="Allowed Classes"
                                        >
                                            {studentClasses.map((studentClass) => (
                                                <MenuItem key={studentClass.id} value={studentClass.name}>
                                                    {studentClass.name}
                                                </MenuItem>
                                            ))}
                                            <MenuItem value="__add_new__">+ Add New Class</MenuItem>
                                        </Select>
                                    </FormControl>
                                    {showAddClassInput && (
                                        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <TextField
                                                label="New Class Name"
                                                value={newClassName}
                                                onChange={(e) => setNewClassName(e.target.value)}
                                                variant="standard"
                                                fullWidth
                                                autoFocus
                                            />
                                            <Button onClick={handleAddNewClass} variant="contained" size="small">
                                                Add
                                            </Button>
                                            <Button onClick={handleCancelAddClass} size="small">
                                                Cancel
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            );
                        }

                        // Special handling for checkedOut (boolean)
                        if (key === "checkedOut") {
                            return (
                                <FormControlLabel
                                    key={key}
                                    control={
                                        <Checkbox
                                            checked={value || false}
                                            onChange={(e) => setNewAsset({ ...newAsset, checkedOut: e.target.checked })}
                                        />
                                    }
                                    label="Checked Out"
                                />
                            );
                        }

                        return (
                            <TextField
                                key={key}
                                label={key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim()}
                                name={key}
                                variant="standard"
                                value={value || ""}
                                onChange={handleChange}
                                multiline={key === "notes"}
                                rows={key === "notes" ? 4 : 1}
                                type={key === "purchaseDate" ? "date" : key === "purchaseCost" ? "number" : "text"}
                                InputLabelProps={key === "purchaseDate" ? { shrink: true } : undefined}
                            />
                        );
                    })}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button 
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                >Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AddInventory;
